import { CbhIcon, Text } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { type ReactNode } from "react";

const SCHEDULE_CHANGE_CARD_COLORS = {
  blue: "rgb(89, 109, 163)",
  green: "rgb(97, 157, 80)",
} as const;

type ScheduleChangeCardColor = keyof typeof SCHEDULE_CHANGE_CARD_COLORS;

interface ScheduleChangeCardProps {
  readonly iconType: "calendar-navigation" | "clock";
  readonly title: string;
  readonly points: string;
  readonly color: ScheduleChangeCardColor;
  readonly lowerText?: ReactNode;
}

function ScheduleChangeCard(props: ScheduleChangeCardProps) {
  const { iconType, title, points, color: colorKey, lowerText } = props;
  const color = SCHEDULE_CHANGE_CARD_COLORS[colorKey];

  return (
    <Box
      sx={(theme) => ({
        backgroundColor: theme.palette.background.paper,
        borderRadius: "16px",
        padding: theme.spacing(4),
        boxShadow: "0px 2px 8px 0px rgba(0,0,0,0.08)",
        display: "flex",
        flexDirection: "row",
        alignItems: lowerText ? "flex-start" : "center",
        gap: theme.spacing(2),
        width: "100%",
      })}
    >
      <CbhIcon
        type={iconType}
        size="medium"
        sx={{
          color,
          flexShrink: 0,
        }}
      />
      <Stack spacing={1} sx={{ flex: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Text variant="body2" sx={{ fontWeight: 500, color }}>
            {title}
          </Text>
          <Text variant="body2" sx={{ fontWeight: 500, color }}>
            {points}
          </Text>
        </Stack>
        {lowerText}
      </Stack>
    </Box>
  );
}

export function ScheduleChangesStep() {
  return (
    <>
      <Text variant="h3" sx={{ fontWeight: 400 }}>
        When your schedule changes, you&apos;re covered
      </Text>
      <Stack spacing={3} sx={{ width: "100%" }}>
        <ScheduleChangeCard
          iconType="calendar-navigation"
          title="Same-day cancellation credit"
          points="+100 points"
          color="blue"
          lowerText={
            <Text
              variant="body2"
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                fontWeight: 400,
                textAlign: "left",
              })}
            >
              <Box component="span" sx={{ fontWeight: 500 }}>
                Tip:
              </Box>{" "}
              Cancelling &gt;24h in advance saves your credit and incurs an even lower penalty.
            </Text>
          }
        />
        <ScheduleChangeCard
          iconType="clock"
          title="Lateness credit"
          points="+50 points"
          color="green"
        />
      </Stack>
    </>
  );
}
